/**
 * Rewrites the images in a pasted signature to point at our own copies.
 *
 * A signature pasted out of Gmail references `googleusercontent` URLs that
 * expire. Left alone, the logo renders correctly on the day it is pasted and
 * disappears weeks later, in vendors' inboxes, where nobody will see it go.
 */
export async function importPastedImages(
  html: string,
  importFn: (url: string) => Promise<string>,
): Promise<string> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const images = [...doc.querySelectorAll('img')];

  const remote = [...new Set(
    images.map((img) => img.getAttribute('src') ?? '')
      .filter((src) => src && !src.startsWith('/api/signature/assets/')),
  )];

  const imported = new Map<string, string | null>();
  await Promise.all(remote.map(async (src) => {
    imported.set(src, await importFn(src).catch(() => null));
  }));

  for (const img of images) {
    const src = img.getAttribute('src') ?? '';
    if (!imported.has(src)) continue;
    const local = imported.get(src);
    if (local) img.setAttribute('src', local);
    else img.remove();
  }

  return doc.body.innerHTML;
}
